// src/pages/Ad/AdProvider.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  CircularProgress,
  Grid,
  Fab,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import AdProviderFilter from '../../components/AdProviderFilter';
import AdProviderDataGrid from '../../components/AdProviderDataGrid';
import AddProviderDialog from '../../components/AddProviderDialog';
import EditProviderDialog from '../../components/EditProviderDialog';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const AdProvider = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();

  // State variables
  const [providers, setProviders] = useState([]);
  const [filters, setFilters] = useState({
    districtId: null,
    talukId: null,
    pincode: null,
    phoneNumber: null,
  });
  const [loading, setLoading] = useState(false);
  const [noProvidersFound, setNoProvidersFound] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [privileges, setPrivileges] = useState({});

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' | 'error' | 'warning' | 'info'

  useEffect(() => {
    // Decode the token to get user level and privileges
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserLevel(payload.level);
        fetchPrivileges(payload.userId);
      } catch (error) {
        console.error('Error decoding token:', error);
        navigate('/login');
      }
    } else {
      // Handle unauthenticated state
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Fetch user privileges (assigned pincodes) based on userId -- used only for filter data scope
  const fetchPrivileges = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/privileges/modules/${userId}`);
      if (response.status === 200 && response.data.modules) {
        setPrivileges(response.data);
      } else {
        console.error('Failed to fetch privileges or no modules found');
      }
    } catch (error) {
      console.error('Error fetching privileges:', error);
    }
  };

  useEffect(() => {
    // Fetch providers based on the current filters
    fetchProviders(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Function to fetch providers based on filters
  const fetchProviders = async (filters) => {
    setLoading(true);
    try {
      if (filters.phoneNumber) {
        // Fetch provider by phone number
        const response = await axios.get(`${API_BASE_URL}/providers/phone/${filters.phoneNumber}`);
        if (response.status === 200 && response.data.provider) {
          setProviders([response.data.provider]);
          setNoProvidersFound(false);
          showSnackbar('Provider found.', 'success');
        } else {
          setProviders([]);
          setNoProvidersFound(true);
          showSnackbar('No provider found with the provided phone number.', 'info');
        }
      } else {
        // Fetch providers based on districtId, talukId, pincode
        const params = {};
        if (filters.districtId) params.districtId = filters.districtId;
        if (filters.talukId) params.talukId = filters.talukId;
        if (filters.pincode) params.pincode = filters.pincode;

        const response = await axios.get(`${API_BASE_URL}/providers/filters`, { params });

        if (response.status === 200 && response.data.providers && response.data.providers.length > 0) {
          setProviders(response.data.providers);
          setNoProvidersFound(false);
        } else {
          setProviders([]);
          setNoProvidersFound(true);
        }
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      setProviders([]);
      setNoProvidersFound(true);
      showSnackbar('No provider fount or Error fetching providers.', 'error');
    }
    setLoading(false);
  };

  // Handler to navigate to provider's ads
  const handleViewDetails = (providerId) => {
    navigate(`/ad/ad-providers/${providerId}/ads`);
  };

  // Handlers for Add Provider Dialog
  const handleOpenAddDialog = () => {
    if (!can('ads.providers.create')) return;
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // Handlers for Edit Provider Dialog
  const handleOpenEditDialog = (provider) => {
    if (!can('ads.providers.edit')) return;
    setSelectedProvider(provider);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setSelectedProvider(null);
    setOpenEditDialog(false);
  };

  // Snackbar handlers
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setSnackbarMessage('');
  };

  return (
    <div style={{ padding: '20px', position: 'relative' }}>
       <Box sx={{ position: 'relative', padding: 2 }}>
                    {/* Place BackButton outside the Grid */}
                    <BackButton />
                    <br/>  <br/>
      {/* Header with Title and Add Button */}
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" align="center" gutterBottom>
            Ad Providers
          </Typography>
        </Grid>
        {can('ads.providers.create') && (
          <Grid item>
            <Fab color="primary" aria-label="add" onClick={handleOpenAddDialog}>
              <AddIcon />
            </Fab>
          </Grid>
        )}
      </Grid>

      {/* Filters */}
      <AdProviderFilter
        userLevel={userLevel}
        privileges={privileges}
        setFilters={setFilters}
        hasAccess={() => can('ads.providers')}
        showSnackbar={showSnackbar}
      />

      {/* Data Grid or Loading/No Data Messages */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress color="primary" />
        </div>
      ) : noProvidersFound ? (
        <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
          No providers found for the selected filters.
        </Typography>
      ) : (
        <AdProviderDataGrid
          providers={providers}
          loading={loading}
          onEdit={can('ads.providers.edit') ? handleOpenEditDialog : undefined}
          onViewDetails={handleViewDetails}
        />
      )}

      {/* Add Provider Dialog */}
      <AddProviderDialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        showSnackbar={showSnackbar}
        refreshData={() => fetchProviders(filters)}
      />

      {/* Edit Provider Dialog */}
      {selectedProvider && (
        <EditProviderDialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          provider={selectedProvider}
          showSnackbar={showSnackbar}
          refreshData={() => fetchProviders(filters)}
        />
      )}

      {/* Snackbar for feedback */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Box>
    </div>
  );
};

export default AdProvider;
